import Hls from "hls.js";
import { ServicePlayer } from "./service_player";
import type { RepeatMode } from "./types";

export class UmgHlsPlayer extends ServicePlayer {
  service = "umg_hls" as const;
  supportsDevices = false;
  private videoElement: HTMLVideoElement | null = null;
  private callback: string;

  constructor(videoElement: HTMLVideoElement | null, callback: string) {
    super();
    this.videoElement = videoElement;
    this.callback = callback;
  }

  changeVideoElement(videoElement: HTMLVideoElement) {
    this.tearDown();
    this.videoElement = videoElement;
    this.init();
  }

  changeCallback(callback: string) {
    this.callback = callback;
  }

  tearDown() {
    if (this.videoElement) {
      this.videoElement.removeEventListener("timeupdate", () => {});
    }
  }

  async init() {
    if (!this.videoElement) {
      console.warn("No video element");
      return;
    }
    this.videoElement.addEventListener("timeupdate", () => {
      // Ignore time update if dispatched less than 1 second ago

      const currentTime =
        (this.videoElement as HTMLVideoElement).currentTime * 1000;
      if (currentTime < this.playbackState.progressMs) {
        // timer has gone down means track has changed
        this.playbackState.progressMs = currentTime;
        this.dispatchState();
      } else if (currentTime - this.playbackState.progressMs >= 1000) {
        this.playbackState.progressMs = currentTime;
        this.dispatchState();
      }
    });

    console.debug("UmgHls player init done");
  }

  private assertVideoElement(): HTMLVideoElement {
    if (!this.videoElement) {
      throw new Error("No video element");
    }
    return this.videoElement;
  }

  async pause() {
    const videoElement = this.assertVideoElement();
    videoElement.pause();

    this.playbackState.isPaused = true;
    this.dispatchState();
  }

  async resume() {
    const videoElement = this.assertVideoElement();
    videoElement.play();

    this.playbackState.isPaused = false;
    this.dispatchState();
  }

  async seek(positionMs: number) {
    const videoElement = this.assertVideoElement();
    videoElement.currentTime = positionMs / 1000;

    this.playbackState.progressMs = positionMs;
    this.dispatchState();
  }

  async nextTrack() {
    console.error("Not supported");
  }

  async previousTrack() {
    console.error("Not supported");
  }

  async playPlaylist(playlistId: string, options?: { position?: number }) {
    console.error("Not supported");
  }

  async playAlbum(albumId: string, options?: { position?: number }) {
    console.error("Not supported");
  }

  async playTrack(trackId: string, options?: { positionMs?: number }) {
    const videoElement = this.assertVideoElement();
    const assetUrl = `https://hls.universal-music.de/get?id=${trackId}&callback=${this.callback}`;
    this.playbackState.isLoading = true;
    this.dispatchState();
    if (Hls.isSupported()) {
      console.debug("Using HLS.js");
      const hls = new Hls();
      hls.loadSource(assetUrl);
      hls.attachMedia(videoElement);
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        videoElement.play();
      });
    } else if (videoElement.canPlayType("application/vnd.apple.mpegurl")) {
      console.debug("Using native HLS");
      const resp = await fetch(assetUrl, { mode: "cors" }); // fetch the redirect url
      videoElement.src = resp.url;
      videoElement.play();
    }
    // Get duration of the track
    const duration = await new Promise<number>((resolve) => {
      videoElement.onloadedmetadata = () => {
        resolve(videoElement.duration * 1000);
      };
    });
    // seek to position if provided
    if (options?.positionMs) {
      videoElement.currentTime = options.positionMs / 1000;
    }

    // TODO: may want to listen for some video element events to set this
    this.playbackState.currentTrack = {
      id: trackId,
      name: "Unknown",
      artists: [],
      album: { name: "Unknown" },
      durationMs: duration,
      image: "",
    };
    this.playbackState.isPaused = false;
    this.playbackState.isLoading = false;
    this.dispatchState();
  }

  async playTracks(trackIds: string[], options?: { position?: number }) {
    console.error("Not supported");
  }

  async setVolume(volume: number) {
    const videoElement = this.assertVideoElement();

    videoElement.volume = volume;
    this.playbackState.volume = volume;
    this.dispatchState();
  }

  async getDevices() {
    console.error("Not supported");
    return [];
  }

  async setDevice(deviceId: string) {
    console.error("Not supported");
  }

  async togglePlayback(shuffle: boolean) {
    console.error("Not supported");
  }

  async setRepeatMode(mode: RepeatMode) {
    console.error("Not supported");
  }

  async enqueueTracks(tracks: string[]) {
    console.error("Not supported");
  }
}
